import type { Asset } from '../types/domain';
import { softenLatexLeakForLanguageCard } from './particlePreviewText';
import { extractStructuredTextParts } from './textHelper';

function toHomeCardDisplayTitle(
  _asset: Pick<Asset, 'subject' | 'ai_category'>,
  value: unknown,
  options?: { allowFileName?: boolean },
): string {
  const title = toDisplayTitle(value, options);
  if (!title) {
    return title;
  }
  // 数学等学科标题常含 $...$ / \frac 等源码，首页卡片统一清洗为可读文本
  return softenLatexLeakForLanguageCard(title);
}

function getMetadataString(metadata: Record<string, unknown> | null | undefined, key: string): string {
  const value = metadata?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeTitleCandidate(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function isCameraFileNameLike(value: string): boolean {
  const baseName = value.split(/[\\/]/).pop()?.trim() || value.trim();
  const withoutExtension = baseName.replace(/\.(?:jpe?g|png|webp|gif|bmp|heic|heif)$/i, '');

  return /^(?:IMG|DSC|PXL|VID|MVIMG|Screenshot|Screen Shot|camera|library|image|photo|wx_camera|mmexport|wechatimg)[-_\s]?\d/i.test(withoutExtension)
    || /^\d{8}[-_]?\d{6}/.test(withoutExtension)
    || /^[0-9a-f]{8}(?:[-_][0-9a-f]{4,}){2,}$/i.test(withoutExtension);
}

export function toDisplayTitle(value: unknown, options?: { allowFileName?: boolean }): string {
  const candidate = normalizeTitleCandidate(value);
  if (!candidate) {
    return '';
  }

  if (/^(?:未命名素材|原始素材|upload|uploaded|image|photo|picture|图片|照片|素材)$/i.test(candidate)) {
    return '';
  }

  if (isCameraFileNameLike(candidate)) {
    return '';
  }

  if (options?.allowFileName && /\.(?:jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(candidate)) {
    return candidate.replace(/\.(?:jpe?g|png|webp|gif|bmp|heic|heif)$/i, '').trim();
  }

  return candidate;
}

export function getAssetFileName(asset: Asset): string {
  const metadata = asset.metadata || {};
  return toDisplayTitle(getMetadataString(metadata, 'originalName'), { allowFileName: true })
    || toDisplayTitle(getMetadataString(metadata, 'fileName'), { allowFileName: true })
    || toDisplayTitle(getMetadataString(metadata, 'name'), { allowFileName: true });
}

export function getParticlePreviewTitle(
  particles: Array<{ title?: string; questions?: unknown }>,
  _options?: { softenLatexForLanguageCard?: boolean },
): string {
  const particle = particles[0];
  if (!particle) {
    return '';
  }

  const questionParts = extractStructuredTextParts(particle.questions, 'question');
  const title = toDisplayTitle(questionParts.main || questionParts.items[0] || particle.title);
  if (!title) {
    return title;
  }
  return softenLatexLeakForLanguageCard(title);
}

export function getAssetTitle(asset: Asset, particlePreview?: string) {
  const metadata = asset.metadata || {};
  const candidates = [
    particlePreview,
    asset.particle_preview,
    asset.ai_title,
    getMetadataString(metadata, 'displayTitle'),
    getMetadataString(metadata, 'title'),
    getMetadataString(metadata, 'assetTitle'),
    getMetadataString(metadata, 'aiTitle'),
    asset.description,
    asset.ai_brief,
    getMetadataString(metadata, 'summary'),
    getMetadataString(metadata, 'brief'),
    getMetadataString(metadata, 'topic'),
    asset.subject ? `${asset.subject}知识素材` : '',
  ];

  for (const candidate of candidates) {
    const title = toHomeCardDisplayTitle(asset, candidate);
    if (title) {
      return title;
    }
  }

  return toHomeCardDisplayTitle(asset, getMetadataString(metadata, 'originalName'), { allowFileName: true }) || '知识素材';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function isFailureStatus(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }

  return /failed|failure|error|rejected|denied|moderation|失败|异常|拒绝/.test(value.trim().toLowerCase());
}

function isPendingExtractionStatus(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }

  return /^(queued|queueing|pending|processing|running|extracting)$/i.test(value.trim());
}

function readParticleExtractionStatus(asset: Pick<Asset, 'metadata' | 'particle_extraction_lock_expires_at'> | null | undefined): string | null {
  if (!asset) {
    return null;
  }

  const metadata = asset.metadata || {};
  const nested = asRecord(metadata.particleExtraction) || asRecord(metadata.particle_extraction);
  const candidates = [
    metadata.particleExtractionStatus,
    metadata.particle_extraction_status,
    metadata.extractionStatus,
    metadata.extraction_status,
    nested?.status,
    nested?.state,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
}

/**
 * 提取进行中：持有未过期锁，或 metadata 已进入 queued/processing
 *（异步入队后往往先写 queued、稍后再抢锁，仅看锁会漏掉 spinning）。
 */
export function isAssetExtractionActive(
  asset: Pick<Asset, 'metadata' | 'particle_extraction_lock_expires_at'> | null | undefined,
) {
  if (!asset) {
    return false;
  }

  if (asset.particle_extraction_lock_expires_at) {
    const expiresAt = new Date(asset.particle_extraction_lock_expires_at).getTime();
    if (Number.isFinite(expiresAt) && expiresAt > Date.now()) {
      return true;
    }
  }

  return isPendingExtractionStatus(readParticleExtractionStatus(asset));
}

export function isAssetExtractionFailed(
  asset: Asset,
  options: { particleCount?: number } = {},
): boolean {
  if (isAssetExtractionActive(asset)) {
    return false;
  }

  if (typeof options.particleCount === 'number' && options.particleCount > 0) {
    return false;
  }

  const metadata = asset.metadata || {};
  const nested = asRecord(metadata.particleExtraction) || asRecord(metadata.particle_extraction);
  const statusCandidates = [
    metadata.particleExtractionStatus,
    metadata.particle_extraction_status,
    metadata.extractionStatus,
    metadata.extraction_status,
    nested?.status,
    nested?.state,
  ];
  const errorCandidates = [
    metadata.particleExtractionError,
    metadata.particle_extraction_error,
    metadata.extractionError,
    metadata.extraction_error,
    nested?.error,
    nested?.message,
  ];
  const aiExtractions: Array<{ status?: string }> = Array.isArray((asset as Asset & { ai_extractions?: unknown[] }).ai_extractions)
    ? ((asset as Asset & { ai_extractions?: Array<{ status?: string }> }).ai_extractions || [])
    : [];

  return statusCandidates.some(isFailureStatus)
    || errorCandidates.some((value) => typeof value === 'string' && value.trim().length > 0)
    || metadata.contentModeration === false
    || aiExtractions.some((extraction) => isFailureStatus(extraction?.status));
}

export function buildAssetListSummary(
  asset: Asset,
  options: { particlePreview?: string; particleCount?: number; particleCountKnown?: boolean } = {},
): string {
  if (options.particleCountKnown === false) {
    const brief = toHomeCardDisplayTitle(asset, asset.ai_brief)
      || toHomeCardDisplayTitle(asset, getMetadataString(asset.metadata || {}, 'summary'))
      || toHomeCardDisplayTitle(asset, getMetadataString(asset.metadata || {}, 'brief'))
      || toHomeCardDisplayTitle(asset, asset.description);
    if (brief) {
      return brief;
    }
    const preview = toHomeCardDisplayTitle(asset, options.particlePreview || asset.particle_preview || undefined);
    if (preview) {
      return `已提取颗粒：${preview}`;
    }
    return '正在加载知识颗粒…';
  }

  if (isAssetExtractionActive(asset)) {
    return '知识颗粒提取进行中…';
  }

  if (isAssetExtractionFailed(asset, options)) {
    return '知识颗粒提取失败，请稍后重试或重新上传。';
  }

  const metadata = asset.metadata || {};
  const brief = toHomeCardDisplayTitle(asset, asset.ai_brief)
    || toHomeCardDisplayTitle(asset, getMetadataString(metadata, 'summary'))
    || toHomeCardDisplayTitle(asset, getMetadataString(metadata, 'brief'))
    || toHomeCardDisplayTitle(asset, asset.description);

  if (brief) {
    return brief;
  }

  const preview = toHomeCardDisplayTitle(asset, options.particlePreview);
  if (preview) {
    return `已提取颗粒：${preview}`;
  }

  if (typeof options.particleCount === 'number' && options.particleCount > 0) {
    return `已提取 ${options.particleCount} 个知识颗粒`;
  }

  return '尚未提取知识颗粒，上传后可自动分析内容。';
}
